// Embedding Service for Way2Will
// Uses Gemini Embeddings API (gemini-embedding-001) for semantic memory

import { GoogleGenAI } from '@google/genai';

const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY || '';
const EMBEDDING_MODEL = 'gemini-embedding-001';
const EMBEDDING_DIMENSION = 768; // Default dimension for gemini-embedding-001

// Task types for optimized embeddings
export type EmbeddingTaskType =
    | 'RETRIEVAL_DOCUMENT'  // For storing documents
    | 'RETRIEVAL_QUERY'     // For search queries
    | 'SEMANTIC_SIMILARITY' // For comparing texts
    | 'CLASSIFICATION'      // For categorization
    | 'CLUSTERING';         // For grouping

export interface EmbeddingResult {
    embedding: number[];
    model: string;
    dimension: number;
}

export interface BatchEmbeddingResult {
    embeddings: number[][];
    model: string;
    dimension: number;
    count: number;
}

// Rate limiting state
let lastRequestTime = 0;
const MIN_REQUEST_INTERVAL = 100; // 100ms between requests

/**
 * Check if embedding service is available
 */
export function isEmbeddingAvailable(): boolean {
    return !!(GEMINI_API_KEY && GEMINI_API_KEY !== 'YOUR_GEMINI_API_KEY_HERE');
}

/**
 * Generate embedding for a single text
 * @param text - Text to embed
 * @param taskType - Type of embedding task (affects optimization)
 * @returns Embedding vector (768 dimensions)
 */
export async function generateEmbedding(
    text: string,
    taskType: EmbeddingTaskType = 'RETRIEVAL_DOCUMENT'
): Promise<EmbeddingResult> {
    if (!isEmbeddingAvailable()) {
        throw new Error('Embedding service not configured: missing GEMINI_API_KEY');
    }

    if (!text || text.trim().length === 0) {
        throw new Error('Cannot generate embedding for empty text');
    }

    // Rate limiting
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTime;
    if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
        await new Promise(resolve => setTimeout(resolve, MIN_REQUEST_INTERVAL - timeSinceLastRequest));
    }
    lastRequestTime = Date.now();

    try {
        const ai = new GoogleGenAI({ apiKey: GEMINI_API_KEY });

        const response = await ai.models.embedContent({
            model: EMBEDDING_MODEL,
            contents: text,
            config: {
                taskType: taskType,
            },
        });

        if (!response.embeddings || response.embeddings.length === 0) {
            throw new Error('No embedding returned from API');
        }

        const embedding = response.embeddings[0].values;

        if (!embedding || embedding.length === 0) {
            throw new Error('Empty embedding values returned');
        }

        console.log(`[EmbeddingService] Generated embedding (${embedding.length} dims) for: "${text.slice(0, 50)}..."`);

        return {
            embedding,
            model: EMBEDDING_MODEL,
            dimension: embedding.length,
        };
    } catch (error) {
        console.error('[EmbeddingService] Error generating embedding:', error);
        throw error;
    }
}

/**
 * Generate embeddings for multiple texts in batch
 * @param texts - Array of texts to embed
 * @param taskType - Type of embedding task
 * @returns Array of embedding vectors
 */
export async function generateEmbeddings(
    texts: string[],
    taskType: EmbeddingTaskType = 'RETRIEVAL_DOCUMENT'
): Promise<BatchEmbeddingResult> {
    if (!isEmbeddingAvailable()) {
        throw new Error('Embedding service not configured');
    }

    if (!texts || texts.length === 0) {
        throw new Error('Cannot generate embeddings for empty array');
    }

    // Filter out empty texts
    const validTexts = texts.filter(t => t && t.trim().length > 0);
    if (validTexts.length === 0) {
        throw new Error('All provided texts are empty');
    }

    // Rate limiting
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTime;
    if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
        await new Promise(resolve => setTimeout(resolve, MIN_REQUEST_INTERVAL - timeSinceLastRequest));
    }
    lastRequestTime = Date.now();

    try {
        const ai = new GoogleGenAI({ apiKey: GEMINI_API_KEY });

        const response = await ai.models.embedContent({
            model: EMBEDDING_MODEL,
            contents: validTexts,
            config: {
                taskType: taskType,
            },
        });

        if (!response.embeddings || response.embeddings.length === 0) {
            throw new Error('No embeddings returned from API');
        }

        const embeddings = response.embeddings.map(e => e.values || []);

        console.log(`[EmbeddingService] Generated ${embeddings.length} embeddings (${embeddings[0]?.length} dims each)`);

        return {
            embeddings,
            model: EMBEDDING_MODEL,
            dimension: embeddings[0]?.length || EMBEDDING_DIMENSION,
            count: embeddings.length,
        };
    } catch (error) {
        console.error('[EmbeddingService] Error generating batch embeddings:', error);
        throw error;
    }
}

/**
 * Generate embedding for a search query
 * Uses RETRIEVAL_QUERY task type for better search performance
 */
export async function generateQueryEmbedding(query: string): Promise<number[]> {
    const result = await generateEmbedding(query, 'RETRIEVAL_QUERY');
    return result.embedding;
}

/**
 * Generate embedding for a document to be stored
 * Uses RETRIEVAL_DOCUMENT task type for storage optimization
 */
export async function generateDocumentEmbedding(document: string): Promise<number[]> {
    const result = await generateEmbedding(document, 'RETRIEVAL_DOCUMENT');
    return result.embedding;
}

/**
 * Calculate cosine similarity between two embeddings
 * @returns Similarity score between -1 and 1 (1 = identical)
 */
export function cosineSimilarity(a: number[], b: number[]): number {
    if (a.length !== b.length) {
        throw new Error(`Vector dimensions must match: ${a.length} vs ${b.length}`);
    }

    let dotProduct = 0;
    let aMagnitude = 0;
    let bMagnitude = 0;

    for (let i = 0; i < a.length; i++) {
        dotProduct += a[i] * b[i];
        aMagnitude += a[i] * a[i];
        bMagnitude += b[i] * b[i];
    }

    if (aMagnitude === 0 || bMagnitude === 0) {
        return 0;
    }

    return dotProduct / (Math.sqrt(aMagnitude) * Math.sqrt(bMagnitude));
}

/**
 * Find top-K most similar embeddings from a list
 * Used as fallback when Firestore vector search is not available
 */
export function findTopKSimilar(
    queryEmbedding: number[],
    documents: Array<{ id: string; embedding: number[] }>,
    k: number = 3
): Array<{ id: string; similarity: number }> {
    const scored = documents.map(doc => ({
        id: doc.id,
        similarity: cosineSimilarity(queryEmbedding, doc.embedding),
    }));

    // Sort by similarity descending and take top K
    return scored
        .sort((a, b) => b.similarity - a.similarity)
        .slice(0, k);
}

/**
 * Normalize an embedding vector (L2 normalization)
 * Useful for some vector databases that expect normalized vectors
 */
export function normalizeEmbedding(embedding: number[]): number[] {
    const magnitude = Math.sqrt(embedding.reduce((sum, val) => sum + val * val, 0));
    if (magnitude === 0) return embedding;
    return embedding.map(val => val / magnitude);
}
